import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import { Box, IconButton, Typography, Button } from "@mui/material";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Dropdown from "../../../../Inputs/Dropdown/Dropdown";
import { http } from "../../../../../clients/http/http.client";
import { HTTP_ENDPOINTS, WEB_STORAGE_KEYS } from "../../../../../clients/clients.constants";
import InputText from "../../../../Inputs/InputText/InputText";
import { setTimeZone } from "../../../../../store/timeZoneSlice";
import Loader from "../../../../Inputs/Loader/Loader";
import ExportModal from "../../../../general/ExportModal";
import { Download, Refresh } from "@mui/icons-material";
import { formatDateTimeInTimeZone } from "../../../../../helpers/useTimeZoneTime";
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const {
  EQUIPMENT_MOVEMENTS,
  STORE_SETTINGS,
  DOWNLOAD_MOVEMENTS_REPORT,
  GENERATED_FILE,
  GET_TRIPS_DATA,
  DOWNLOAD_TRIPS_REPORT
} = HTTP_ENDPOINTS;

const defaultPage = 1;

const CommonTable = ({ fixtures, matHandling, isMovements, dataEndpoint, columns, tableName, dataType, downloadUrl, defaultSort = '', siteId }) => {
  const location = useLocation();
  const [isDebounceRunning, setIsDebounceRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaginating, setIsPaginating] = useState(false);
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState(defaultPage);
  const [isEndReached, setIsEndReached] = useState(false);
  const [sortKey, setSortKey] = useState(defaultSort || "");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState(null);
  const [movementData, setMovementData] = useState([]);
  const [fileGenerationLoading, setIsFileGenerationLoading] = useState(false);
  const [error, setError] = useState(null);

  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const dateRange = useSelector(state => state.analyticsSelections.dateRange);
  const timeZone = useSelector((state) => state.timeZone.timeZone);

  const observer = useRef();
  const cancelTokenSourceRef = useRef(null);
  
  // Use a callback ref for the loader element to guarantee it exists when the observer is attached.
  const loaderRef = useCallback(node => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !isEndReached && !isPaginating) {
        setPage(prevPage => prevPage + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [isLoading, isPaginating, isEndReached]);


  const tableContainerRef = useRef(null);

  const getTableHeight = useCallback(() => {
    if (location.search.includes('backroom-material')) return 'calc(100vh - 390px)';
    if (location.search.includes('fixtures') || location.search.includes('arrc-inventory') || location.search.includes('shopping-carts')) return 'calc(100vh - 390px)';
    return 'calc(100vh - 350px)';
  }, [location.search]);

  const buildSiteIdQueryParams = useCallback((currentPage) => {
    const params = new URLSearchParams();
    if (dateRange?.start && dateRange?.end) {
      const startDT = DateTime.fromISO(dateRange.start);
      const endDT = DateTime.fromISO(dateRange.end);
      if (startDT.isValid && endDT.isValid) {
        params.append("from", startDT.toUTC().toISO());
        params.append("to", endDT.toUTC().toISO());
      }
    }
    if (siteId) params.append("siteId", siteId);
    if (search) params.append("search", search);
    params.append("sortDirection", sortOrder);
    if (fixtures?.length > 0) params.append("equipmentTypeIds", fixtures.join(","));
    if (matHandling?.length > 0) params.append("location", matHandling.join(","));
    if (sortKey) params.append("sortField", sortKey);
    params.append("page", currentPage);
    params.append("limit", limit);
    return params.toString() ? `?${params.toString()}` : "";
  }, [dateRange, siteId, search, sortOrder, fixtures, matHandling, sortKey, limit]);

  const fetchData = useCallback(async (isInitial = true) => {
    if (isInitial) {
      setIsLoading(true);
      setPage(defaultPage);
      setMovementData([]);
    } else {
      setIsPaginating(true);
    }
    setError(null);

    const currentPage = isInitial ? defaultPage : page;
    const filters = buildSiteIdQueryParams(currentPage);
    
    const source = axios.CancelToken.source();
    cancelTokenSourceRef.current = source;

    try {
      // Fetch timezone on initial load
      if (isInitial && siteId) {
        const storeSettingsResponse = await http.client.get(`${STORE_SETTINGS}/${siteId}`, {
          cancelToken: source.token,
        });
        dispatch(setTimeZone(storeSettingsResponse.TimeZone));
      }

      const res = await http.client.get(`${dataEndpoint}${filters}`, { cancelToken: source.token });
      if (!res.data || !Array.isArray(res.data)) {
        throw new Error('Invalid response format');
      }

      if (isInitial) {
        setMovementData(res.data);
      } else {
        setMovementData(prev => [...prev, ...res.data]);
      }
      
      if (res.data.length < limit) {
        setIsEndReached(true);
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        setError(err.message || "Failed to fetch data.");
      }
    } finally {
      if (isInitial) setIsLoading(false);
      else setIsPaginating(false);
    }
  }, [buildSiteIdQueryParams, dataEndpoint, limit, page]);

  // Initial fetch and re-fetch on filter change
  useEffect(() => {
    setIsEndReached(false);
    fetchData(true);
  }, [search, siteId, sortKey, sortOrder, fixtures, matHandling, dateRange, location.search]);

  // Paginated fetch
  useEffect(() => {
    if (page > defaultPage && !isLoading) {
      fetchData(false);
    }
  }, [page]);

  const handleRetry = () => {
    setError(null);
    if (movementData.length === 0) {
      fetchData(true);
    } else {
      fetchData(false);
    }
  };

  const checkGeneratedFile = useCallback(async () => {
    try {
      const email = user.user.preferred_username;
      const res = await http.client.get(
        `${GENERATED_FILE}?dataType=${dataType}&email=${encodeURIComponent(email)}`
      );
      setGeneratedFiles(res.files && res.files.length > 0 ? res.files : []);
    } catch (error) {
      console.error("Error checking generated file:", error);
      setGeneratedFiles([]);
    }
  }, [user.user.preferred_username, dataType]);

  const openModal = useCallback(async () => {
    setModalOpen(true);
    await checkGeneratedFile();
  }, [checkGeneratedFile]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleAllTripsDownload = useCallback(async (fileType) => {
    const source = axios.CancelToken.source();
    cancelTokenSourceRef.current = source;
    try {
      setIsFileGenerationLoading(true);
      const filters = buildSiteIdQueryParams();
      await http.client.get(
        `${downloadUrl}${filters}&fileType=${fileType.toLowerCase()}`,
        {
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setExportProgress(percentCompleted);
              setIsExporting(true);
            }
          },
          cancelToken: source.token,
        }
      );
      setExportProgress(100);
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Export error:", error);
      }
    } finally {
      setIsExporting(false);
      setIsFileGenerationLoading(false);
      setExportProgress(0);
      cancelTokenSourceRef.current = null;
      checkGeneratedFile();
    }
  }, [buildSiteIdQueryParams, downloadUrl, checkGeneratedFile]);

  const formatDuration = useCallback((totalMinutes) => {
    const days = Math.floor(totalMinutes / 1440);
    const remainder = totalMinutes % 1440;
    const hours = Math.floor(remainder / 60);
    const minutes = Math.floor(remainder % 60);
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
    return parts.join(" ");
  }, []);

  const getRowContent = (column, row) => {
    if (column.label === "Date") {
      return formatDateTimeInTimeZone(row?.[column.dataKey], timeZone).split(" ")[0];
    }
    if (column.label === "Time") {
      return formatDateTimeInTimeZone(row?.[column.dataKey], timeZone)
        .split(" ")
        .slice(1)
        .join(" ");
    }
    if (column.label === "Duration") {
      if (row[column.dataKey]) {
        return formatDuration(row[column.dataKey]);
      } else {
        const rowDateTime = DateTime.fromISO(row.dateTime);
        const now = DateTime.now();
        const diffInMinutes = now.diff(rowDateTime, "minutes").minutes;
        return formatDuration(diffInMinutes);
      }
    }
    return row[column.dataKey];
  };

  return (
    <Paper
      sx={{
        width: "100%",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0px 4px 40px rgba(226, 236, 249, 0.60)",
        marginTop: "20px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 20px",
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E5E5E5",
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#1B3665" }}>{tableName}</Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
          <InputText value={search} onChangeFn={setSearch} withDebounce={true} debounceDelay={1000} />
          <Dropdown value={sortKey} onChangeFn={setSortKey} options={columns.filter((col) => col.dataKey !== "dateTime").map((col) => ({ name: col.label, value: col.dataKey }))} placeHolder="Sort by:" widthString="200px" />
          <Dropdown value={sortOrder} onChangeFn={setSortOrder} options={[{ name: 'Ascending', value: 'ASC' }, { name: 'Descending', value: 'DESC' }]} placeHolder="Order:" widthString="200px" />
          <IconButton aria-label="export" color="primary" onClick={openModal}><Download /></IconButton>
          <ExportModal
            open={modalOpen}
            onClose={closeModal}
            fileName={`${dataType}-report_${Date.now()}`}
            handleDownloadAll={handleAllTripsDownload}
            onCancelExport={() => {
              if (cancelTokenSourceRef.current) {
                cancelTokenSourceRef.current.cancel('Export canceled by user');
              }
            }}
            dataType={dataType}
            isMovements={false}
            progress={exportProgress}
            generatedFiles={generatedFiles}
            fileGenerationLoading={fileGenerationLoading}
            isExporting={isExporting}
          />
        </Box>
      </Box>
      
      <TableContainer ref={tableContainerRef} component={Paper} sx={{ height: getTableHeight(), width: "100%", boxShadow: "none", overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Loader />
          </Box>
        ) : error && movementData.length === 0 ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <Typography color="error">Error: {error}</Typography>
            <Button variant="contained" onClick={handleRetry} startIcon={<Refresh />}>Retry</Button>
          </Box>
        ) : (
          <Table stickyHeader sx={{ borderCollapse: "separate", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={`${column.dataKey}-${column.label}`} variant="head" align="left" style={{ width: column.width }} sx={{ textTransform: "uppercase", fontSize: "14px", fontWeight: "500", color: "#718096", background: "#fff", padding: "8px 10px", position: 'sticky', top: 0, zIndex: 1 }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
               {!isLoading && movementData.length === 0 ? (
                 <TableRow>
                   <TableCell colSpan={columns.length} sx={{ textAlign: 'center', padding: '40px' }}>
                     <Typography variant="body2" color="textSecondary">No data available</Typography>
                   </TableCell>
                 </TableRow>
               ) : (
                 movementData.map((row, index) => (
                   <TableRow key={`${row.Entity_key || row.tagId || index}`} hover>
                     {columns.map((column) => (
                       <TableCell key={`${column.dataKey}-${column.label}`} align="left" sx={{ fontSize: "12px", fontWeight: "500", padding: "8px 10px", color: '#2D3748' }}>
                         {getRowContent(column, row)}
                       </TableCell>
                     ))}
                   </TableRow>
                 ))
               )}
               {/* Sentinel Element for IntersectionObserver */}
               <TableRow ref={loaderRef} sx={{ height: '1px' }}>
                 <TableCell colSpan={columns.length} sx={{ padding: '16px', border: 'none', textAlign: 'center' }}>
                    {isPaginating && <Loader />}
                    {error && !isPaginating && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="error">Failed to load more. {error}</Typography>
                        <Button size="small" variant="outlined" onClick={handleRetry} startIcon={<Refresh />}>Retry</Button>
                      </Box>
                    )}
                    {isEndReached && movementData.length > 0 && (
                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>No more data to load</Typography>
                    )}
                 </TableCell>
               </TableRow>
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Paper>
  );
};

export default CommonTable;